/**
 * Day and night on the platform palette.
 *
 * The palette itself lives in `styles.css`; this decides which of its two
 * modes the screen is in. The rules are the ones every surface follows: the
 * browser's own preference on a first visit, the owner's choice after that,
 * remembered per browser and never shared between them.
 *
 * Everything here is pure apart from `applyTheme`, which sets one attribute -
 * so the rules can be tested without a browser.
 */

import { browserStore } from './sections';

export type ThemeMode = 'day' | 'night';

const KEY = 'brunel.command.theme';

/** What a mode looks like coming back out of storage, which can hold anything. */
export function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'day' || value === 'night';
}

interface StringStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The element the mode is set on: `<html>`, which is what `:root` matches. */
interface ThemeRoot {
  setAttribute(name: string, value: string): void;
  getAttribute(name: string): string | null;
}

/**
 * The mode to open in: what was chosen last time, or - having never been
 * chosen - whatever the machine is set to. A store that refuses to be read
 * is the same as a store with nothing in it.
 */
export function recallTheme(store: StringStore | null, prefersNight: boolean): ThemeMode {
  const fallback: ThemeMode = prefersNight ? 'night' : 'day';
  if (store === null) return fallback;
  try {
    const raw = store.getItem(KEY);
    return isThemeMode(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
}

/** Remember the choice. A browser with storage turned off simply does not. */
export function rememberTheme(store: StringStore | null, mode: ThemeMode): void {
  if (store === null) return;
  try {
    store.setItem(KEY, mode);
  } catch {
    /* nothing to say to the owner about this */
  }
}

/** Put the mode on the root element, where every token in the sheet reads it. */
export function applyTheme(root: ThemeRoot, mode: ThemeMode): void {
  root.setAttribute('data-theme', mode);
}

/** The mode the screen is in right now, read back off the root element. */
export function currentTheme(root: ThemeRoot, prefersNight = false): ThemeMode {
  const attribute = root.getAttribute('data-theme');
  return isThemeMode(attribute) ? attribute : (prefersNight ? 'night' : 'day');
}

/** The other one. */
export function otherTheme(mode: ThemeMode): ThemeMode {
  return mode === 'night' ? 'day' : 'night';
}

/** Does this machine ask for a dark screen? A browser that cannot say means no. */
export function prefersNight(): boolean {
  try {
    const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    return media?.matches === true;
  } catch {
    return false;
  }
}

function rootElement(): ThemeRoot | null {
  return (globalThis as { document?: { documentElement?: ThemeRoot } }).document?.documentElement ?? null;
}

/** The owner picked a mode: paint it and remember it. */
export function setTheme(mode: ThemeMode): void {
  const root = rootElement();
  if (root !== null) applyTheme(root, mode);
  rememberTheme(browserStore(), mode);
}

/**
 * Settle the mode before the screen is drawn.
 *
 * `index.html` does this first, inline, so there is no flash of the wrong
 * mode; this repeats it for any entry that did not, and is what tells the
 * app which mode it opened in.
 */
export function startTheme(): ThemeMode {
  const mode = recallTheme(browserStore(), prefersNight());
  const root = rootElement();
  if (root !== null) applyTheme(root, mode);
  return mode;
}
